import React from 'react'
import styled from 'styled-components'
import { colors, layout, bp } from '../../../theme'

const Main = styled.header`
  background:
    radial-gradient(1100px 500px at 15% -10%, ${colors.greenLight} 0%, transparent 60%),
    ${colors.white};
  border-bottom: 1px solid ${colors.line};
`

const Container = styled.div`
  max-width: ${layout.maxWidth};
  margin: auto;
  padding: 4.5rem 1.5rem 3.5rem;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: ${bp.tablet}) {
    grid-template-columns: 1fr;
    padding: 3rem 1.25rem 2.5rem;
    gap: 2rem;
  }
`

const Copy = styled.div``

const Eyebrow = styled.div`
  display: inline-block;
  background: ${colors.greenLight};
  color: ${colors.greenDark};
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  margin-bottom: 1.1rem;
`

const Title = styled.h1`
  font-size: 3rem;
  line-height: 1.08;
  color: ${colors.ink};
  margin: 0 0 1rem;

  span {
    color: ${colors.green};
  }

  @media (max-width: ${bp.mobile}) {
    font-size: 2.1rem;
  }
`

const Tagline = styled.p`
  font-weight: 800;
  color: ${colors.red};
  letter-spacing: 1px;
  margin: 0 0 1rem;
  font-size: 1rem;
`

const Lead = styled.p`
  font-size: 1.12rem;
  line-height: 1.7;
  color: ${colors.slate};
  margin: 0 0 1.75rem;
  max-width: 40rem;

  @media (max-width: ${bp.mobile}) {
    font-size: 1rem;
  }
`

const Actions = styled.div`
  display: flex;
  gap: 0.9rem;
  flex-wrap: wrap;
`

const Primary = styled.a`
  text-decoration: none;
  background: ${colors.green};
  color: ${colors.white};
  font-weight: 700;
  padding: 0.9rem 1.5rem;
  border-radius: 10px;
  transition: 0.2s ease;

  &:hover {
    background: ${colors.greenDark};
    transform: translateY(-2px);
  }
`

const Secondary = styled.a`
  text-decoration: none;
  background: ${colors.white};
  color: ${colors.ink};
  border: 1.5px solid ${colors.line};
  font-weight: 700;
  padding: 0.9rem 1.5rem;
  border-radius: 10px;
  transition: 0.2s ease;

  &:hover {
    border-color: ${colors.green};
    color: ${colors.green};
  }
`

const Panel = styled.div`
  background: ${colors.ink};
  border-radius: ${layout.radius};
  padding: 2rem;
  color: ${colors.white};
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    right: -40px;
    top: -40px;
    width: 160px;
    height: 160px;
    background: ${colors.yellow};
    opacity: 0.14;
    border-radius: 50%;
  }
`

const PanelTitle = styled.h3`
  margin: 0 0 0.4rem;
  font-size: 1.25rem;
  color: ${colors.yellow};
`

const PanelText = styled.p`
  margin: 0 0 1.5rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.96rem;
`

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.1rem;
`

const Stat = styled.div`
  h4 {
    margin: 0;
    font-size: 1.7rem;
    color: ${colors.white};
  }
  p {
    margin: 0.15rem 0 0;
    font-size: 0.82rem;
    color: rgba(255, 255, 255, 0.7);
  }
`

// All figures are sourced from the company profile (assets/), never invented.
const stats = [
  { n: '35+', label: 'Product SKUs' },
  { n: '6', label: 'Product categories' },
  { n: '2018', label: 'Supplying since' },
  { n: 'KES 10', label: 'Minimum per use' },
]

const Hero = () => (
  <Main id="top">
    <Container>
      <Copy>
        <Eyebrow>Manufacturer · Retailer · Supplier</Eyebrow>
        <Title>
          Kenyan-made detergents and the <span>NBIoT EcoSmart Dispenser</span>
        </Title>
        <Tagline>CLEAN. STERILE. TRUSTED.</Tagline>
        <Lead>
          Desparclean Sterix Holdings manufactures, retails, and supplies over 35
          high-performance cleaning detergents under the Despar Clean brand — with a
          specialist strength in hard-water and borehole formulations — and builds the
          NBIoT EcoSmart Dispenser, a smart ATM that dispenses bulk detergent by the
          litre, paid via M-Pesa from as low as KES 10 per use.
        </Lead>
        <Actions>
          <Primary href="#products">Explore the Product Range</Primary>
          <Secondary href="#dispenser">See the Smart Dispenser</Secondary>
        </Actions>
      </Copy>

      <Panel>
        <PanelTitle>Refill-based hygiene, at national scale</PanelTitle>
        <PanelText>
          From our Nairobi manufacturing hub we supply institutions, retailers, and
          households nationwide — with proven roots across Nairobi, Kitui, and Makueni.
        </PanelText>
        <StatGrid>
          {stats.map((s) => (
            <Stat key={s.label}>
              <h4>{s.n}</h4>
              <p>{s.label}</p>
            </Stat>
          ))}
        </StatGrid>
      </Panel>
    </Container>
  </Main>
)

export default Hero
